import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Autocomplete,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import styled from '@emotion/styled';
import { FixedSizeList as List } from 'react-window';
import { Row, SubHeaderTitle, Wrapper, WrapperCard, WrapperContent, WrapperSubHeader } from '../../components/common';
import { axiosFunction } from '../../components/commonFunctions';
import { v2ReactURL } from '../../utils/constant';
import { useNavigate } from 'react-router-dom';

// Updated Types based on API response
interface Product {
  id: number;
  text: string;
}

interface Customer {
  contactId: number;
  firstName: string;
  lastName: string;
  mobileNo: string;
  email: string;
  address: string | null;
  comapanyName: string | null;
  code: string | null;
  fullAddress: string;
}

interface DeliverySlot {
  deliverySlotId: number;
  deliverySlotName: string;
  startTime: string;
  endTime: string;
  isActive: number;
  calculatedCutOffTime: string;
  maximumOrders: number;
  cutOffHours: number;
  deliverySlotType: string | null;
}

interface SubscriptionFormState {
  customer: Customer | null;
  product: Product | null;
  deliverySlot: DeliverySlot | null;
  planType: 'Daily' | 'Custom';
  quantities: { [key: string]: number };
  selectedDays: string[];
  startDate: Dayjs | null;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  background-color: #f8fafc;
  padding: 4px;
  border-radius: 8px;
  
  .MuiToggleButton-root {
    border: none;
    border-radius: 6px;
    margin: 0 4px;
    padding: 6px 16px;
    text-transform: none;
    color: #64748b;
    font-weight: 500;
    transition: all 0.15s ease-in-out;
  }

  .MuiToggleButton-root.Mui-selected {
    background-color: #00a4e5;
    color: #fff;
    box-shadow: 0 4px 8px rgba(37,99,235,0.15);
  }
`;

const DayButton = styled(Button)<{ selected?: boolean }>`
  background-color: ${p => p.selected ? '#00a4e5' : '#f0f0f0'};
  color: ${p => p.selected ? '#fff' : '#00a4e5'};
  padding: 4px 10px;
  margin: 2px;
  min-width: 36px;
  border-radius: 6px;
  text-transform: none;
  font-weight: 600;
  font-size: 0.85rem;
  &:hover { opacity: 0.95 }
`;

const DayRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none }
`;

const ALL_DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Custom hook for debounce
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// VirtualizedList removed — using InfiniteScrollList for Autocomplete lists

// Infinite Scroll List Component with Load More - FIXED VERSION
const InfiniteScrollList = React.forwardRef<HTMLDivElement, any>(
  function InfiniteScrollList(props, ref) {
    const { children, hasMore, loading, onLoadMore, ...other } = props;
    const items = React.Children.toArray(children);
    const itemCount = items.length + (hasMore ? 1 : 0);
    const itemSize = 48;

    const LoaderRow = () => (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          padding: 2,
          height: itemSize
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {loading ? 'Loading more...' : 'Scroll to load more'}
        </Typography>
      </Box>
    );

    const outerElementType = React.forwardRef<HTMLDivElement, any>(
      (outerProps, outerRef) => (
        <div ref={outerRef} {...outerProps} />
      )
    );

    return (
      <div ref={ref} {...other}>
        <List
          width={'100%'}
          height={Math.min(400, itemCount * itemSize)}
          itemCount={itemCount}
          itemSize={itemSize}
          overscanCount={5}
          outerElementType={outerElementType}
          onItemsRendered={({ visibleStopIndex }) => {
            if (visibleStopIndex >= items.length - 5 && hasMore && !loading) {
              onLoadMore?.();
            }
          }}
        >
          {({ index, style }) => (
            <div style={style}>
              {index < items.length ? items[index] : <LoaderRow />}
            </div>
          )}
        </List>
      </div>
    );
  }
);

const NewSubscription: React.FC = () => {
   const navigate = useNavigate()
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [deliverySlots, setDeliverySlots] = useState<DeliverySlot[]>([]);
  const [loading, setLoading] = useState({
    products: false,
    customers: false,
    deliverySlots: false
  });

  const [productSearch, setProductSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [deliverySlotSearch, setDeliverySlotSearch] = useState('');
  
  const debouncedProductSearch = useDebounce(productSearch, 500);
  const debouncedCustomerSearch = useDebounce(customerSearch, 500);
  const debouncedDeliverySlotSearch = useDebounce(deliverySlotSearch, 500);

  const [productPagination, setProductPagination] = useState({
    page: 1,
    limit: 100,
    hasMore: true,
    totalLoaded: 0
  });

  const [customerPagination, setCustomerPagination] = useState({
    page: 1,
    limit: 100,
    hasMore: true,
    totalLoaded: 0
  });

  const [deliverySlotPagination, setDeliverySlotPagination] = useState({
    page: 1,
    limit: 100,
    hasMore: true,
    totalLoaded: 0
  });

  const [formState, setFormState] = useState<SubscriptionFormState>({
    customer: null,
    product: null,
    deliverySlot: null,
    planType: 'Daily',
    quantities: ALL_DAYS.reduce((acc, d) => (acc[d] = 1, acc), {} as { [k:string]: number }),
    selectedDays: [...ALL_DAYS],
    startDate: dayjs()
  });

  console.log(formState.deliverySlot)

  const fetchProducts = useCallback(async (searchValue: string = '', page: number = 1, isLoadMore: boolean = false) => {
    if (!isLoadMore) {
      setLoading(prev => ({ ...prev, products: true }));
    }
    
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/productVariantList?searchValue=${searchValue}&page=${page}&limit=${productPagination.limit}`,
        "GET"
      );
      
      const newProducts = res.data.response || [];
      const totalLoaded = isLoadMore ? productPagination.totalLoaded + newProducts.length : newProducts.length;
      
      if (isLoadMore) {
        setProducts(prev => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }
      
      setProductPagination(prev => ({
        ...prev,
        page,
        hasMore: newProducts.length === productPagination.limit,
        totalLoaded
      }));
      
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  }, [productPagination.limit, productPagination.totalLoaded]);

  // Fetch Customers with Pagination
  const fetchCustomers = useCallback(async (searchValue: string = '', page: number = 1, isLoadMore: boolean = false) => {
    if (!isLoadMore) {
      setLoading(prev => ({ ...prev, customers: true }));
    }
    
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/customerlist?searchValue=${searchValue}&page=${page}&limit=${customerPagination.limit}`,
        "GET"
      );
      
      const newCustomers = res.data.response || [];
      const totalLoaded = isLoadMore ? customerPagination.totalLoaded + newCustomers.length : newCustomers.length;
      
      if (isLoadMore) {
        setCustomers(prev => [...prev, ...newCustomers]);
      } else {
        setCustomers(newCustomers);
      }
      
      setCustomerPagination(prev => ({
        ...prev,
        page,
        hasMore: newCustomers.length === customerPagination.limit,
        totalLoaded
      }));
      
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(prev => ({ ...prev, customers: false }));
    }
  }, [customerPagination.limit, customerPagination.totalLoaded]);

  // Fetch Delivery Slots with Pagination and Search
  const fetchDeliverySlots = useCallback(async (searchValue: string = '', page: number = 1, isLoadMore: boolean = false) => {
    if (!isLoadMore) {
      setLoading(prev => ({ ...prev, deliverySlots: true }));
    }
    
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/delivery/deliverySlotList?searchValue=${searchValue}&page=${page}&limit=${deliverySlotPagination.limit}`,
        "GET"
      );
      
      const newDeliverySlots = res.data.response || [];
      const totalLoaded = isLoadMore ? deliverySlotPagination.totalLoaded + newDeliverySlots.length : newDeliverySlots.length;
      
      if (isLoadMore) {
        setDeliverySlots(prev => [...prev, ...newDeliverySlots]);
      } else {
        setDeliverySlots(newDeliverySlots);
      }
      
      setDeliverySlotPagination(prev => ({
        ...prev,
        page,
        hasMore: newDeliverySlots.length === deliverySlotPagination.limit,
        totalLoaded
      }));
      
    } catch (error) {
      console.error('Error fetching delivery slots:', error);
    } finally {
      setLoading(prev => ({ ...prev, deliverySlots: false }));
    }
  }, [deliverySlotPagination.limit, deliverySlotPagination.totalLoaded]);

  // Load more products
  const loadMoreProducts = useCallback(() => {
    if (!loading.products && productPagination.hasMore) {
      fetchProducts(debouncedProductSearch, productPagination.page + 1, true);
    }
  }, [loading.products, productPagination.hasMore, productPagination.page, debouncedProductSearch, fetchProducts]);

  // Load more customers
  const loadMoreCustomers = useCallback(() => {
    if (!loading.customers && customerPagination.hasMore) {
      fetchCustomers(debouncedCustomerSearch, customerPagination.page + 1, true);
    }
  }, [loading.customers, customerPagination.hasMore, customerPagination.page, debouncedCustomerSearch, fetchCustomers]);

  // Load more delivery slots
  const loadMoreDeliverySlots = useCallback(() => {
    if (!loading.deliverySlots && deliverySlotPagination.hasMore) {
      fetchDeliverySlots(debouncedDeliverySlotSearch, deliverySlotPagination.page + 1, true);
    }
  }, [loading.deliverySlots, deliverySlotPagination.hasMore, deliverySlotPagination.page, debouncedDeliverySlotSearch, fetchDeliverySlots]);

  // Load delivery slots on component mount
  useEffect(() => {
    fetchDeliverySlots();
    fetchCustomers();
    fetchProducts();
  }, []);

  // Fetch products when search term changes
  useEffect(() => {
    if (debouncedProductSearch !== undefined) {
      fetchProducts(debouncedProductSearch, 1, false);
    }
  }, [debouncedProductSearch, fetchProducts]);

  // Fetch customers when search term changes
  useEffect(() => {
    if (debouncedCustomerSearch !== undefined) {
      fetchCustomers(debouncedCustomerSearch, 1, false);
    }
  }, [debouncedCustomerSearch, fetchCustomers]);

  // Fetch delivery slots when search term changes
  useEffect(() => {
    if (debouncedDeliverySlotSearch !== undefined) {
      fetchDeliverySlots(debouncedDeliverySlotSearch, 1, false);
    }
  }, [debouncedDeliverySlotSearch, fetchDeliverySlots]);

  const toggleDay = (day: string) => {
    setFormState(prev => {
      const selected = prev.selectedDays.includes(day);
      return {
        ...prev,
        selectedDays: selected ? prev.selectedDays.filter(d => d !== day) : [...prev.selectedDays, day],
        quantities: selected ? { ...prev.quantities, [day]: 0 } : { ...prev.quantities, [day]: 1 }
      };
    });
  };

  const changeQuantity = (day: string, delta: number) => {
    setFormState(prev => ({
      ...prev,
      quantities: { ...prev.quantities, [day]: Math.max(0, (prev.quantities[day]||0) + delta) }
    }));
  };

  // FIXED: Improved value comparison functions
  const isOptionEqualToValue = (option: any, value: any) => {
    if (!option || !value) return false;
    
    // Compare products by id
    if (option.id && value.id) {
      return option.id === value.id;
    }
    
    // Compare customers by contactId
    if (option.contactId && value.contactId) {
      return option.contactId === value.contactId;
    }
    
    // Compare delivery slots by deliverySlotId
    if (option.deliverySlotId && value.deliverySlotId) {
      return option.deliverySlotId === value.deliverySlotId;
    }
    
    return false;
  };

  // FIXED: Get option key for better performance
  const getOptionKey = (option: any) => {
    if (option.id) return option.id;
    if (option.contactId) return option.contactId;
    if (option.deliverySlotId) return option.deliverySlotId;
    return JSON.stringify(option);
  };

  // FIXED: Get delivery slot label with time
  const getDeliverySlotLabel = (slot: DeliverySlot) => {
    if (!slot) return '';
    return `${slot.deliverySlotName} (${slot.startTime} - ${slot.endTime})`;
  };

const submitSubscription = async () => {
  console.log("Form State:", formState);
  
  // Validation
  if (!formState.customer) {
    console.error('Please select a customer');
    return;
  }
  if (!formState.product) {
    console.error('Please select a product');
    return;
  }
  if (!formState.deliverySlot) {
    console.error('Please select a delivery slot');
    return;
  }
  if (!formState.startDate) {
    console.error('Please select a start date');
    return;
  }

  // Check if at least one day has quantity > 0
  const hasValidQuantity = Object.values(formState.quantities).some(qty => qty > 0);
  if (!hasValidQuantity) {
    console.error('Please set quantity for at least one day');
    return;
  }

  // Prepare the payload
  const payload = {
    contactId: formState.customer.contactId,
    productId: formState.product.id,
    deliverySlotId: formState.deliverySlot.deliverySlotId,
    planType: formState.planType.toLowerCase(),
    startDate: dayjs(formState.startDate).format('YYYY-MM-DD'),
    
    // Initialize all quantities to 0
    sundayQty: 0,
    mondayQty: 0,
    tuesdayQty: 0,
    wednesdayQty: 0,
    thursdayQty: 0,
    fridayQty: 0,
    saturdayQty: 0,
  };

  // Set quantities based on plan type
  if (formState.planType === 'Custom') {
    // For Custom plan, only set quantities for selected days
    formState.selectedDays.forEach(day => {
      const quantity = formState.quantities[day] || 0;
      switch(day) {
        case 'Sunday': payload.sundayQty = quantity; break;
        case 'Monday': payload.mondayQty = quantity; break;
        case 'Tuesday': payload.tuesdayQty = quantity; break;
        case 'Wednesday': payload.wednesdayQty = quantity; break;
        case 'Thursday': payload.thursdayQty = quantity; break;
        case 'Friday': payload.fridayQty = quantity; break;
        case 'Saturday': payload.saturdayQty = quantity; break;
      }
    });
  } else {
    // For non-Custom plans, include all days with their quantities
    payload.sundayQty = formState.quantities.Sunday || 0;
    payload.mondayQty = formState.quantities.Monday || 0;
    payload.tuesdayQty = formState.quantities.Tuesday || 0;
    payload.wednesdayQty = formState.quantities.Wednesday || 0;
    payload.thursdayQty = formState.quantities.Thursday || 0;
    payload.fridayQty = formState.quantities.Friday || 0;
    payload.saturdayQty = formState.quantities.Saturday || 0;
  }

  console.log('Final Payload:', payload);

  try {
    const res = await axiosFunction(
      `${v2ReactURL}/native/saveSubScription`, // Fixed: matches curl endpoint
      "POST", 
      payload,
      false // fileType = false for JSON
    );
    console.log("res?.status",res?.status)
    if (res?.status === 200 || res?.status === 201) {
       navigate("/subscription")
      console.log('Subscription saved successfully!', res.data);
      // Add success notification or redirect here
    } else {
      console.error('Unexpected response:', res);
    }
 
  } catch (err) {
    console.error('Error saving subscription:', err);
    // You might want to show an error message to the user here
  }
};

  return (
    <Wrapper navUrl={"New Subscription"} customStyle={{ height: '750px' }}>
      <WrapperSubHeader className="mainBreadcrumb">
        <div className="mr-auto"><SubHeaderTitle subTitle={"New Subscription"} /></div>
      </WrapperSubHeader>
      <WrapperContent>
        <Row>
          <WrapperCard isCollapsableAnalytics={false}>
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Card>
                <CardContent>
                  <Grid container spacing={3}>
                    {/* Customer Selection */}
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={customers}
                        loading={loading.customers}
                        getOptionLabel={(customer: Customer) => 
                          `${customer.firstName} ${customer.lastName} - ${customer.mobileNo}`
                        }
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Select Customer*" 
                            size="small" 
                            InputLabelProps={{ sx: { fontWeight: 500 } }}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                          />
                        )}
                        value={formState.customer}
                        onChange={(_, value) => setFormState(s => ({ ...s, customer: value }))}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={(x) => x}
                        ListboxComponent={(props) => (
                          <InfiniteScrollList
                            {...props}
                            hasMore={customerPagination.hasMore}
                            loading={loading.customers}
                            onLoadMore={loadMoreCustomers}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={getOptionKey(option)}>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {option.firstName} {option.lastName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {option.mobileNo} • {option.email}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Grid>
                  
                    {/* Product Selection */}
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={products}
                        loading={loading.products}
                        getOptionLabel={(product: Product) => product.text}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Select Product*" 
                            size="small" 
                            InputLabelProps={{ sx: { fontWeight: 500 } }}
                            onChange={(e) => setProductSearch(e.target.value)}
                          />
                        )}
                        value={formState.product}
                        onChange={(_, value) => setFormState(s => ({ ...s, product: value }))}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={(x) => x}
                        ListboxComponent={(props) => (
                          <InfiniteScrollList
                            {...props}
                            hasMore={productPagination.hasMore}
                            loading={loading.products}
                            onLoadMore={loadMoreProducts}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={getOptionKey(option)}>
                            <Typography noWrap title={option.text}>
                              {option.text}
                            </Typography>
                          </li>
                        )}
                      />
                    </Grid>

                    {/* Delivery Slot Selection - UPDATED with search */}
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={deliverySlots}
                        loading={loading.deliverySlots}
                        getOptionLabel={getDeliverySlotLabel}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Select Delivery Slot*" 
                            size="small" 
                            InputLabelProps={{ sx: { fontWeight: 500 } }}
                            onChange={(e) => setDeliverySlotSearch(e.target.value)}
                          />
                        )}
                        value={formState.deliverySlot}
                        onChange={(_, value) => {
                          console.log('Delivery slot selected:', value);
                          setFormState(s => ({ ...s, deliverySlot: value }));
                        }}
                        isOptionEqualToValue={isOptionEqualToValue}
                        getOptionKey={getOptionKey}
                        filterOptions={(x) => x}
                        ListboxComponent={(props) => (
                          <InfiniteScrollList
                            {...props}
                            hasMore={deliverySlotPagination.hasMore}
                            loading={loading.deliverySlots}
                            onLoadMore={loadMoreDeliverySlots}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={getOptionKey(option)}>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {option.deliverySlotName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {option.startTime} - {option.endTime}
                                {option.deliverySlotType && ` • ${option.deliverySlotType}`}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Grid>

                    {/* Rest of the component remains the same */}
                    {/* Plan Type Selection */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Select Plan Type <span className='text-danger'>*</span>
                      </Typography>
                      <StyledToggleButtonGroup
                        value={formState.planType}
                        exclusive
                        onChange={(_, value) => value && setFormState(s => ({ ...s, planType: value }))}
                      >
                        <ToggleButton value={'Daily'}>Daily</ToggleButton>
                        <ToggleButton value={'Custom'}>Custom</ToggleButton>
                      </StyledToggleButtonGroup>

                      {/* Custom Plan Days Selection */}
                      {formState.planType === 'Custom' && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                            Select Days
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
                            {ALL_DAYS.map(day => (
                              <DayButton 
                                key={day} 
                                selected={formState.selectedDays.includes(day)} 
                                onClick={() => toggleDay(day)}
                              >
                                {day.slice(0, 3)}
                              </DayButton>
                            ))}
                          </Box>
                          
                          {/* Quantity Controls for Selected Days */}
                          <Box sx={{ mt: 2 }}>
                            {ALL_DAYS.map(day => (
                              <Box 
                                key={day} 
                                sx={{ 
                                  display: formState.selectedDays.includes(day) ? 'flex' : 'none', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between', 
                                  mb: 1,
                                  p: 1,
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: 1
                                }}
                              >
                                <Typography sx={{ width: 150, fontWeight: 600 }}>{day}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => changeQuantity(day, -1)} 
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ mx: 2, fontWeight: 500, minWidth: '20px', textAlign: 'center' }}>
                                    {formState.quantities[day] ?? 0}
                                  </Typography>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => changeQuantity(day, 1)} 
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Daily Plan - All Days with Quantities */}
                      {formState.planType === 'Daily' && (
                        <Box sx={{ mt: 2 }}>
                          {ALL_DAYS.map(day => (
                            <DayRow key={day}>
                              <Typography sx={{ fontWeight: 700 }}>{day}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => changeQuantity(day, -1)} 
                                  sx={{ border: '1px solid', borderColor: '#21262b', p: '6px' }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 500, mx: 0.5 }}>
                                  {formState.quantities[day]}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => changeQuantity(day, 1)} 
                                  sx={{ border: '1px solid', borderColor: '#21262b', p: '6px' }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </DayRow>
                          ))}
                        </Box>
                      )}
                    </Grid>

                    {/* Start Date */}
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Select Start Date*"
                          value={formState.startDate}
                          onChange={newVal => setFormState(s => ({ ...s, startDate: newVal }))}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>

                  </Grid>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, alignItems: 'center' }}>
                    <Button variant="outlined" color="error" onClick={() => navigate('/subscription')}>
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={submitSubscription} 
                      sx={{ bgcolor: '#00a4e5', '&:hover': { bgcolor: '#0088c5' } }}
                    >
                      Save
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Container>
          </WrapperCard>
        </Row>
      </WrapperContent>
    </Wrapper>
  );
}

export default NewSubscription;